import { useState, type FormEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { ClipboardList, Filter, PenLine, RotateCcw, Search, UserCog } from "lucide-react";
import { Button, Card, Input, Label, PageHeader, Select } from "../components/ui";
import { api } from "../lib/api";
import { Link } from "../lib/router";

type Tab = "checklist" | "acessos";

function ChecklistTab() {
  const [draftBusca, setDraftBusca] = useState("");
  const [busca, setBusca] = useState("");
  const [tecnico, setTecnico] = useState("");
  const [empresaAtendida, setEmpresaAtendida] = useState("");
  const [dataDe, setDataDe] = useState("");
  const [dataAte, setDataAte] = useState("");

  const checklists = useQuery({
    queryKey: ["checklists-formatacao", busca, tecnico, empresaAtendida, dataDe, dataAte],
    queryFn: () =>
      api.listChecklistsFormatacao({
        busca: busca || undefined,
        tecnico: tecnico || undefined,
        empresaAtendida: empresaAtendida || undefined,
        dataDe: dataDe || undefined,
        dataAte: dataAte || undefined,
      }),
  });

  function applyFilters(event: FormEvent) {
    event.preventDefault();
    setBusca(draftBusca.trim());
  }

  function clearFilters() {
    setDraftBusca("");
    setBusca("");
    setTecnico("");
    setEmpresaAtendida("");
    setDataDe("");
    setDataAte("");
  }

  const itens = checklists.data ?? [];

  return (
    <>
      <Card className="form-section">
        <form onSubmit={applyFilters} className="form-grid">
          <div>
            <Label htmlFor="f-busca">Buscar (usuário, série, chamado, protocolo...)</Label>
            <Input
              id="f-busca"
              value={draftBusca}
              onChange={(event) => setDraftBusca(event.target.value)}
              placeholder="Digite e pressione Buscar"
            />
          </div>
          <div>
            <Label htmlFor="f-tecnico">Técnico</Label>
            <Input id="f-tecnico" value={tecnico} onChange={(event) => setTecnico(event.target.value)} />
          </div>
          <div>
            <Label htmlFor="f-empresa">Empresa atendida</Label>
            <Input
              id="f-empresa"
              value={empresaAtendida}
              onChange={(event) => setEmpresaAtendida(event.target.value)}
            />
          </div>
          <div />
          <div>
            <Label htmlFor="f-data-de">Data de atendimento (de)</Label>
            <Input id="f-data-de" type="date" value={dataDe} onChange={(event) => setDataDe(event.target.value)} />
          </div>
          <div>
            <Label htmlFor="f-data-ate">Data de atendimento (até)</Label>
            <Input id="f-data-ate" type="date" value={dataAte} onChange={(event) => setDataAte(event.target.value)} />
          </div>
          <div className="flex items-end gap-2 md:col-span-2">
            <Button type="submit">
              <Search className="size-4" />
              Buscar
            </Button>
            <Button type="button" variant="secondary" onClick={clearFilters}>
              <RotateCcw className="size-4" />
              Limpar filtros
            </Button>
          </div>
        </form>
      </Card>

      <Card className="form-section">
        {checklists.isLoading ? (
          <p className="text-sm text-slate-500">Carregando checklists...</p>
        ) : itens.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center text-slate-500">
            <Filter className="size-6" />
            <p className="text-sm">Nenhum checklist encontrado com esses filtros.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="ticket-table">
              <thead>
                <tr>
                  <th>Protocolo</th>
                  <th>Data</th>
                  <th>Técnico</th>
                  <th>Empresa atendida</th>
                  <th>Usuário / equipamento</th>
                  <th>Chamado</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {itens.map((item) => (
                  <tr key={item.Id}>
                    <td>#{item.Id}</td>
                    <td>{new Date(item.DataAtendimento).toLocaleDateString("pt-BR")}</td>
                    <td>{item.Tecnico}</td>
                    <td>{item.EmpresaAtendida}</td>
                    <td>{item.UsuarioEquipamento}</td>
                    <td>{item.ChamadoId ? `#${item.ChamadoId}` : "—"}</td>
                    <td>
                      {item.status === "rascunho" ? (
                        <span className="status-badge status-badge--warning">Em andamento</span>
                      ) : (
                        <span className="status-badge status-badge--success">Concluído</span>
                      )}
                    </td>
                    <td>
                      {item.status === "rascunho" ? (
                        <Link to={`/checklist-formatacao/${item.Id}/editar`} className="button button--ghost button--sm">
                          <PenLine className="size-4" />
                          Continuar preenchendo
                        </Link>
                      ) : (
                        <Link to={`/checklist-formatacao/${item.Id}`} className="button button--ghost button--sm">
                          <ClipboardList className="size-4" />
                          Ver checklist
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}

function AcessosTab() {
  const [draftBusca, setDraftBusca] = useState("");
  const [busca, setBusca] = useState("");
  const [solicitante, setSolicitante] = useState("");
  const [empresaSolicitante, setEmpresaSolicitante] = useState("");
  const [tipo, setTipo] = useState("");
  const [dataDe, setDataDe] = useState("");
  const [dataAte, setDataAte] = useState("");

  const solicitacoes = useQuery({
    queryKey: ["access-requests", busca, solicitante, empresaSolicitante, tipo, dataDe, dataAte],
    queryFn: () =>
      api.listAccessRequests({
        busca: busca || undefined,
        solicitante: solicitante || undefined,
        empresaSolicitante: empresaSolicitante || undefined,
        tipo: tipo || undefined,
        dataDe: dataDe || undefined,
        dataAte: dataAte || undefined,
      }),
  });

  function applyFilters(event: FormEvent) {
    event.preventDefault();
    setBusca(draftBusca.trim());
  }

  function clearFilters() {
    setDraftBusca("");
    setBusca("");
    setSolicitante("");
    setEmpresaSolicitante("");
    setTipo("");
    setDataDe("");
    setDataAte("");
  }

  const itens = solicitacoes.data ?? [];

  return (
    <>
      <Card className="form-section">
        <form onSubmit={applyFilters} className="form-grid">
          <div>
            <Label htmlFor="a-busca">Buscar (colaborador, chamado, protocolo...)</Label>
            <Input
              id="a-busca"
              value={draftBusca}
              onChange={(event) => setDraftBusca(event.target.value)}
              placeholder="Digite e pressione Buscar"
            />
          </div>
          <div>
            <Label htmlFor="a-solicitante">Solicitante</Label>
            <Input id="a-solicitante" value={solicitante} onChange={(event) => setSolicitante(event.target.value)} />
          </div>
          <div>
            <Label htmlFor="a-empresa">Empresa solicitante</Label>
            <Input
              id="a-empresa"
              value={empresaSolicitante}
              onChange={(event) => setEmpresaSolicitante(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="a-tipo">Tipo</Label>
            <Select id="a-tipo" value={tipo} onChange={(event) => setTipo(event.target.value)}>
              <option value="">Todos</option>
              <option value="criacao">Criação</option>
              <option value="desativacao">Desativação</option>
            </Select>
          </div>
          <div>
            <Label htmlFor="a-data-de">Data da solicitação (de)</Label>
            <Input id="a-data-de" type="date" value={dataDe} onChange={(event) => setDataDe(event.target.value)} />
          </div>
          <div>
            <Label htmlFor="a-data-ate">Data da solicitação (até)</Label>
            <Input id="a-data-ate" type="date" value={dataAte} onChange={(event) => setDataAte(event.target.value)} />
          </div>
          <div className="flex items-end gap-2 md:col-span-2">
            <Button type="submit">
              <Search className="size-4" />
              Buscar
            </Button>
            <Button type="button" variant="secondary" onClick={clearFilters}>
              <RotateCcw className="size-4" />
              Limpar filtros
            </Button>
          </div>
        </form>
      </Card>

      <Card className="form-section">
        {solicitacoes.isLoading ? (
          <p className="text-sm text-slate-500">Carregando solicitações...</p>
        ) : itens.length === 0 ? (
          <div className="flex flex-col items-center gap-2 py-10 text-center text-slate-500">
            <Filter className="size-6" />
            <p className="text-sm">Nenhuma solicitação encontrada com esses filtros.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="ticket-table">
              <thead>
                <tr>
                  <th>Protocolo</th>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Solicitante</th>
                  <th>Empresa</th>
                  <th>Colaborador</th>
                  <th>Chamado</th>
                  <th>Validação</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {itens.map((item) => (
                  <tr key={item.Id}>
                    <td>#{item.Id}</td>
                    <td>{new Date(item.DataSolicitacao).toLocaleDateString("pt-BR")}</td>
                    <td>{item.tipo === "criacao" ? "Criação" : "Desativação"}</td>
                    <td>{item.NomeSolicitante}</td>
                    <td>{item.EmpresaSolicitante}</td>
                    <td>{item.NomeColaborador || item.NomeDesativado || "—"}</td>
                    <td>{item.ChamadoId ? `#${item.ChamadoId}` : "—"}</td>
                    <td>
                      {item.StatusValidacao === "concluido" ? (
                        <span className="status-badge status-badge--success">Concluída</span>
                      ) : (
                        <span className="status-badge status-badge--warning">Pendente</span>
                      )}
                    </td>
                    <td>
                      <Link to={`/acessos/${item.Id}`} className="button button--ghost button--sm">
                        <UserCog className="size-4" />
                        Ver solicitação
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </>
  );
}


export function ChecklistFormatacaoListPage() {
  const [tab, setTab] = useState<Tab>("checklist");

  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Equipe Mira Sistemas"
        title="Formulários da equipe"
        description="Consulte os checklists de formatação e as solicitações de acesso registrados pela equipe."
      />

      <div className="tab-bar">
        <button
          type="button"
          className={tab === "checklist" ? "tab-button tab-button--active" : "tab-button"}
          onClick={() => setTab("checklist")}
        >
          <ClipboardList className="size-4" />
          Checklists de formatação
        </button>
        <button
          type="button"
          className={tab === "acessos" ? "tab-button tab-button--active" : "tab-button"}
          onClick={() => setTab("acessos")}
        >
          <UserCog className="size-4" />
          Solicitações de acesso
        </button>
      </div>

      {tab === "checklist" && <ChecklistTab />}
      {tab === "acessos" && <AcessosTab />}
    </div>
  );
}
